import React, {useEffect, useState} from 'react'
import {observer} from 'mobx-react'
import './webGL.scss'
import Cookies from 'js-cookie'
import {routes} from "../App";
import ConditionalComponent from "../Components/Shared/ConditionalComponent";
import Utility from "../Utility";
import Settings from "../settings.json";
import WebGLLoader from "./WebGLLoader";
import {useStores} from "../Hooks";
import {history} from "../index";
import Unity, {UnityContext} from "react-unity-webgl";
import {permissionsEnum, procedureTypeEnum} from "../serverClientShared/sharedEnums";
import {useParams} from "react-router";
import sharedUtils from "../serverClientShared/sharedUtils";
import {ProcedureRequester} from "../MobxStores/RequestApi/ProcedureRequester";
import {GeneralRequester} from "../MobxStores/RequestApi/GeneralRequester";

const WEBGL_MANAGER_NAME = "webgl_manager";
const REMOTE_ASSETS_RECEIVER_NAME = "Procedure Tool Interaction Controller";
const storageUrl = Settings.cdnUrl + "/global/webgl";

const unityContext = new UnityContext({
    loaderUrl: storageUrl + "/webgl-3d-player/webgl.loader.js",
    dataUrl: storageUrl + "/webgl-3d-player/webgl.data.unityweb?v=" + Settings.buildTimestamp,
    frameworkUrl: storageUrl + "/webgl-3d-player/webgl.framework.js.unityweb?v=" + Settings.buildTimestamp,
    codeUrl: storageUrl + "/webgl-3d-player/webgl.wasm.unityweb?v=" + Settings.buildTimestamp,
    streamingAssetsUrl: storageUrl + "/webgl-3d-player/StreamingAssets",
});

const baseUrl = Utility.getWebglBaseUrl();

const urlParams = new URLSearchParams(window.location.search);
const token = Cookies.get(Settings.tokenCookieName) || '';
const WebGL3dPlayer = observer(({twoDProcedure, procedure, isPreview}) => {
    const {generalStore, projectStore, procedureStore, modalStore} = useStores();
    const [loading, setLoading] = useState(true);
    const [webGLData, setWebGLData] = useState(null);
    const [progression, setProgression] = useState(0);

    const matchParams = isPreview ? {} : useParams();
    unityContext.on("GetProjectDetails", getProjectDetails);
    unityContext.on("Exit3DProcedure", exit3D);
    unityContext.on("SelectMediaFiles", selectTextureFile);
    unityContext.on("OpenPdfInNewTab", openPdfInNewTab);
    unityContext.on("GetRemoteAssets", getRemoteAssets)

    async function getRemoteAssets (assetsGuids, workspace)  {
        const assetsGuidsObj = JSON.parse(assetsGuids);
        const assetsGuidsArray = assetsGuidsObj.values || [];
        const token2 = !token.length ? (await GeneralRequester.getGuestToken()).Token : token;
        const remoteAssets = await ProcedureRequester.getRemoteAssets(workspace, assetsGuidsArray, token2);
        unityContext.send(REMOTE_ASSETS_RECEIVER_NAME, "ReceiveRemoteAssets", JSON.stringify(remoteAssets));
    }

    useEffect(function () {
        unityContext.on("progress", function (progression) {
            setProgression(progression);
        })
    }, []);

    useEffect(() => {
        if (progression === 1) {
            setLoading(false);
        }
    }, [progression]);

    useEffect(() => {
        document.addEventListener("click", onMouseClick);
        const twoDProcedureId = urlParams.get('connected_procedure') || matchParams.connectedProcedure || twoDProcedure?.ProcedureIdentifier;
        const modeIndex = urlParams.get('mode_index') || matchParams.modeIndex;

        generalStore.forceStopDataPoll = true;
        const db = generalStore.workspace;
        if (!procedure) {
            history.push(routes.NOT_FOUND);
        }

        let currentProcedure = procedure;
        let permissions = sharedUtils.getPermissionSet(generalStore?.userDetails?.Domains ? generalStore?.userDetails?.Domains[generalStore?.workspace] : null, generalStore.domainSettings, {}, true);
        let canSeeCad = projectStore.checkAllowedCurrentProject(permissionsEnum.GET_CAD_DATA, false);

        (async function fetchData(){
            projectStore.getProjectById(procedure.ProjectRef).then(project => {
                projectStore.currentProject = project;
            })

            try {
                const publishedProcedure = await ProcedureRequester.getPublishedProcedure(procedure.ProcedureIdentifier, db)
                if (publishedProcedure) {
                    currentProcedure = publishedProcedure;
                }
            } catch (e) {
                console.error('Procedure is not published');
            }

            if ((currentProcedure?.PayPerClickId && currentProcedure?.PayPerClickId.length > 0) || currentProcedure?.IsProjectPublic) {
                for (let key in permissionsEnum) {
                    if (!key.includes('VIRTUAL') && !permissions.includes(permissionsEnum[key])) {
                        permissions.push(permissionsEnum[key]);
                    }
                }
            }
            const permissionsSet = new Set(permissions);
            sharedUtils.deleteUnpermittedPermissions(generalStore.domainSettings || generalStore.domainSettings, permissionsSet);


            if (!canSeeCad) {
                permissionsSet.delete(permissionsEnum.GET_CAD_DATA);
                permissionsSet.delete(permissionsEnum.EDIT_CAD_DATA);
            }

            const newWebGLData = {
                token,
                deviceId: Cookies.get('deviceId_v2'),
                doceboParams: procedureStore.doceboParams,
                baseUrl,
                db,
                project: !!projectStore.currentProject ? {ProjectIdentifier: procedure.ProjectRef} : projectStore.currentProject,
                procedure: currentProcedure,
                twoDProcedureId,
                modeIndex,
                canSeeCad,
                permissions: [...permissionsSet],
                theme: generalStore.theme,
                isGuest: !token.length
            };
            if (procedure.ProcedureType === procedureTypeEnum.THREE_D) {
                newWebGLData.isEditMode = !window.location.pathname.includes('/view/') && !window.location.pathname.includes('/run/');
            }
            if (procedure) {
                setWebGLData(newWebGLData);
            }

            return () => {
                exit3D();
            };
        })();
        // eslint-disable-next-line
    }, []);

    function openPdfInNewTab(url) {
        window.open(url, '_blank');
    }
    
    function exit3D() {
        document.removeEventListener("click", onMouseClick);
        unityContext.send(WEBGL_MANAGER_NAME, "WebGLDisableCaptureAllInput");
        generalStore.forceStopDataPoll = false;
        procedureStore.play3d = false;
        window.close();
    }


    function onMouseClick(event) {
        if (loading) {
            return;
        }
        try {
            if (event.path[0].tagName !== 'CANVAS') {
                unityContext.send(WEBGL_MANAGER_NAME, "WebGLDisableCaptureAllInput");
            } else {
                unityContext.send(WEBGL_MANAGER_NAME, "WebGLEnableCaptureAllInput");
            }
        } catch (e) {
            console.error(e);
        }
    }

    async function getProjectDetails() {
        if (!Object.keys(projectStore.currentProject).length || !Object.keys(webGLData.project).length) {
            projectStore.getProjectById(procedure.ProjectRef).then(project => {
                webGLData.project = !!project ? {ProjectIdentifier: procedure.ProjectRef} : project;
                projectStore.currentProject = project;
            })
        }
        setTimeout(() => {
            const modifiedWebGLData = removeKeysFromWebGLData(webGLData, 'procedure', ['ObjectTargetItems', 'LocalMaterialDataList']);
            const neWebGLData = JSON.stringify(modifiedWebGLData);
            if (baseUrl.includes('dev') || baseUrl.includes('localhost')) {
                console.log('webgl data')
                console.log(neWebGLData)
            }
           unityContext.send(WEBGL_MANAGER_NAME, "SetInfo", neWebGLData);
        }, 1000);
    }

    function removeKeysFromWebGLData(data, parentKey, keysToRemove) {
        const modifiedData = data;
        
        if (modifiedData[parentKey]) {
            keysToRemove.forEach(key => {
                if (modifiedData[parentKey][key]) {
                    delete modifiedData[parentKey][key];
                }
            });
        }
        return modifiedData;
    }


    async function selectTextureFile() {
        const fullscreenElement = document.fullscreenElement;
        if (fullscreenElement) {
            document.exitFullscreen().then();
        }
        modalStore.modalData = {
            preventRefreshProject: true,
            onMediaSelected: async (files) => {
                if (!files.length) {
                    return;
                }
                const file = files[0];
                unityContext.send(WEBGL_MANAGER_NAME, "MediaFilesSelected", file.FileId);
                if (fullscreenElement) {
                    fullscreenElement.requestFullscreen().then();
                }
            },
            onlyImages: true,
        };
        modalStore.isMediaLibraryModalOpen = true;
    }

    return (
        <div className='webgl-container'>
            <div
                id='unityContainer'
                className={'unity-container three-d-container' + (generalStore.isInFullscreen ? ' in-fullscreen' : '')}
            >
                <ConditionalComponent if={!!webGLData}>
                    <Unity unityContext={unityContext}/>
                </ConditionalComponent>
            </div>

            <ConditionalComponent if={loading}>
                <WebGLLoader initialProgression={progression}/>
            </ConditionalComponent>
        </div>
    )
})

export default WebGL3dPlayer;